import { Link } from "react-router-dom";
import { MapPin, Clock, Mountain, Lightbulb, Waves, Bus } from "lucide-react";
import { L } from "./destData";
import { Cover } from "./parts";

function Attraction({ a, i, p, place }) {
  const [name, desc, tip] = a;
  return (
    <li className="rounded-3xl bg-white overflow-hidden shadow-sm ring-1 ring-[#0B2A4A]/5 flex flex-col" data-testid={`dest-attraction-${i}`}>
      {p && <figure className="relative" data-testid={`dest-attraction-photo-${i}`}>
        <Cover src={p.src} alt={`${name}, ${place}`} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" className="w-full aspect-[3/2] object-cover" />
        <a href={p.source} target="_blank" rel="noopener noreferrer" className="absolute bottom-1.5 right-2 left-2 text-right text-[9px] text-white/80 drop-shadow line-clamp-1">{p.credit}</a>
      </figure>}
      <div className="p-5 flex-1 flex flex-col">
        <p className="text-[11px] tracking-[0.3em] text-[#B08D57]">{String(i + 1).padStart(2, "0")}</p>
        <h3 className="pp-serif text-xl text-[#0B2A4A] mt-1">{name}</h3>
        <p className="text-sm text-[#3B4656] mt-2 leading-relaxed">{desc}</p>
        <p className="text-sm text-[#0B2A4A] mt-3 flex gap-2"><Lightbulb className="w-4 h-4 shrink-0 mt-0.5 text-[#B08D57]" />{tip}</p>
      </div>
    </li>
  );
}

const Block = ({ icon: I, title, children, testid }) => (
  <div className="rounded-3xl bg-white ring-1 ring-[#0B2A4A]/10 p-6" data-testid={testid}>
    <h3 className="pp-serif text-xl text-[#0B2A4A] flex items-center gap-2"><I className="w-5 h-5 text-[#B08D57]" />{title}</h3>
    <div className="text-sm text-[#3B4656] mt-3 leading-relaxed">{children}</div>
  </div>
);

export default function DestGuide({ g, name, arts, lang, photos = {} }) {
  if (!g) return null;
  const it = g.itinerary;
  const more = arts.find((x) => /da vedere|cosa vedere/i.test(x.it_title || ""));
  const ad = /^[aeiouAEIOU]/.test(name) ? "ad" : "a";
  return (
    <section className="px-4 sm:px-10 pt-12 sm:pt-20" data-testid="dest-guide">
      <h2 className="pp-serif text-3xl text-[#0B2A4A] mb-8">{lang === "it" ? `Le 7 cose da vedere ${ad} ${name}` : L(lang, "Le 7 cose da vedere in {name}", "7 things to see in {name}").replace("{name}", name)}</h2>
      <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dest-attractions">{g.attractions.map((a, i) => <Attraction key={a[0]} a={a} i={i} p={photos[i]} place={name} />)}</ul>
      {more && <p className="mt-6"><Link to={more.path} className="text-sm font-semibold underline text-[#0B2A4A]" data-testid="dest-attractions-more">{L(lang, "Approfondisci nella guida completa", "Read the full guide")}: {more.title} →</Link></p>}
      <div className="grid lg:grid-cols-2 gap-5 mt-12">
        <div className="rounded-3xl bg-[#0B2A4A] text-white p-6 sm:p-8" data-testid="dest-itinerary">
          <p className="text-[11px] tracking-[0.3em] text-[#E9DCC3]">{L(lang, "ITINERARIO CONSIGLIATO", "SUGGESTED ITINERARY")}</p>
          <h3 className="pp-serif text-2xl mt-2">{it.title}</h3>
          <p className="text-sm text-white/80 mt-3 flex flex-wrap gap-x-5 gap-y-1"><span className="flex items-center gap-1"><Clock className="w-4 h-4" />{it.duration}</span><span className="flex items-center gap-1"><Mountain className="w-4 h-4" />{it.difficulty}</span></p>
          <ol className="mt-5 space-y-2">{it.stops.map((s, i) => <li key={s} className="flex gap-3 text-sm"><span className="w-6 h-6 rounded-full bg-white/15 grid place-items-center text-xs shrink-0">{i + 1}</span><span className="pt-0.5">{s}</span></li>)}</ol>
          <p className="text-xs text-white/70 mt-5">{it.note}</p>
        </div>
        <Block icon={MapPin} title={L(lang, "I consigli dei local", "Local tips")} testid="dest-tips"><ul className="space-y-2 list-disc pl-5">{g.tips.map((t) => <li key={t}>{t}</li>)}</ul></Block>
        <Block icon={Waves} title={L(lang, "Spiagge e natura", "Beaches and nature")} testid="dest-nature"><p>{g.nature}</p></Block>
        <Block icon={Bus} title={L(lang, "Come arrivare e muoversi", "Getting there and around")} testid="dest-arrive"><p>{g.arrive}</p></Block>
      </div>
    </section>
  );
}
